import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { BonusCalculator } from '../components/BonusCalculator';
import { PayoutSpeedCheck } from '../components/PayoutSpeedCheck';
import { GameMix } from '../components/GameMix';
import { SafetyChecklist } from '../components/SafetyChecklist';
import { FAQAccordion } from '../components/FAQAccordion';
import { offers } from '../data/offers';
import { FiArrowRight, FiCheck, FiShield, FiLock, FiClock, FiDollarSign, FiStar, FiTrendingUp, FiInfo, FiAlertCircle, FiCreditCard } from 'react-icons/fi';

const heroAnchors = [
  { id: 'calculator', label: 'Bonus Calculator' },
  { id: 'payouts', label: 'Payout Speed' },
  { id: 'games', label: 'Game Mix' },
  { id: 'safety', label: 'Safety Check' },
  { id: 'offers', label: 'Top Offers' },
  { id: 'faq', label: 'FAQ' },
];

const scrollToSection = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

export const Home: React.FC = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Your Casinos',
    url: 'https://yourscasinos.com',
    description: 'Compare UK casino offers, bonuses, and payout speeds. Find the best casino deals with transparent reviews.',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://yourscasinos.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <>
      <SEO
        title="UK Casino Offers Comparison | Best Bonuses & Fast Payouts | Your Casinos"
        description="Compare the best UK casino offers with transparent reviews. Find casinos with fast payouts, generous bonuses, and trusted licenses. Start playing smarter today."
        canonical="/"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-b from-black via-black to-gray-900 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/heromaincard.webp"
            alt="Casino table"
            className="w-full h-full object-cover opacity-20"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/80"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div>
              <h1 className="text-5xl md:text-7xl font-bold mb-6">
                <span className="text-white">Find Your</span>{' '}
                <span className="text-primary-green">Perfect Casino</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8">
                Compare bonuses, payout speeds, and trusted UK-licensed casinos. Get the real numbers before you deposit.
              </p>
              
              {/* Hero Anchors */}
              <div className="flex flex-wrap justify-center gap-3 mb-8">
                {heroAnchors.map(anchor => (
                  <button
                    key={anchor.id}
                    onClick={() => scrollToSection(anchor.id)}
                    className="glass-card px-4 py-2 text-sm font-medium hover:bg-primary-green/20 transition-colors"
                  >
                    {anchor.label}
                  </button>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/compare" className="btn-primary inline-flex items-center justify-center">
                  Compare All Offers
                  <FiArrowRight className="ml-2" />
                </Link>
                <button
                  onClick={() => scrollToSection('offers')}
                  className="btn-secondary inline-flex items-center justify-center"
                >
                  View Top Casinos
                </button>
              </div>
            </div>

            {/* Hero Image */}
            <div className="hidden lg:block">
              <div className="glass-card p-4 rounded-xl overflow-hidden">
                <img
                  src="/images/heromaincard2.webp"
                  alt="Casino gaming table"
                  className="w-full h-auto rounded-lg"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Top Offers Section */}
      <section id="offers" className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Top UK Casino Offers
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Hand-picked casinos with the best bonuses, fastest payouts, and trusted UK licenses.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {offers.map(offer => (
              <div key={offer.slug} className="glass-card p-6 hover:border-primary-green transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <img
                    src={offer.logo}
                    alt={offer.name}
                    className="h-12 w-auto"
                    loading="lazy"
                  />
                  <div className="text-right">
                    <div className="text-2xl font-bold text-primary-green">
                      {offer.rating10}/10
                    </div>
                    <div className="text-xs text-gray-400">Rating</div>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{offer.name}</h3>
                <p className="text-primary-green font-medium mb-2">{offer.bonusHeadline}</p>
                <p className="text-gray-400 text-sm mb-4">{offer.payoutHeadline}</p>
                <div className="flex gap-2 mb-4">
                  <Link
                    to={`/offers/${offer.slug}`}
                    className="btn-primary flex-1 text-center text-sm py-2"
                  >
                    Read Review
                  </Link>
                  <a
                    href={offer.officialUrl}
                    target="_blank"
                    rel="noreferrer noopener sponsored"
                    className="btn-secondary flex-1 text-center text-sm py-2"
                  >
                    Official Site
                  </a>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/compare" className="btn-primary inline-flex items-center">
              Compare All Offers
              <FiArrowRight className="ml-2" />
            </Link>
          </div>
        </div>
      </section>
      {/* Bonus Calculator Section */}
      <section id="calculator" className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-white mb-4">
              Calculate Your Bonus Value
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              See exactly how much bonus you'll get and what wagering requirements apply. No surprises, just clear numbers.
            </p>
          </div>
          <BonusCalculator />
        </div>
      </section>

      {/* Payout Speed Section */}
      <section id="payouts" className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-white mb-4">
              Check Payout Processing Times
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Different payment methods mean different wait times. Compare speeds and choose what works for you.
            </p>
          </div>
          <PayoutSpeedCheck />
        </div>
      </section>

      {/* Game Mix Section */}
      <section id="games" className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-white mb-4">
              Explore Game Collections
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              From slots to live dealers, see what's available and find casinos that match your gaming style.
            </p>
          </div>
          <GameMix />
        </div>
      </section>

      {/* Safety Checklist Section */}
      <section id="safety" className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-white mb-4">
              Safety Standards We Verify
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Every casino we review must meet these essential safety requirements. Your security comes first.
            </p>
          </div>
          <SafetyChecklist />
        </div>
      </section>


      {/* Official Sites Block */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-white mb-4">
              Visit Official Casino Sites
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Direct links to trusted UK-licensed casinos. Claim your bonuses and start playing today.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {offers.map(offer => (
              <a
                key={offer.slug}
                href={offer.officialUrl}
                target="_blank"
                rel="noreferrer noopener sponsored"
                className="glass-card p-6 hover:border-primary-green transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1 group-hover:text-primary-green transition-colors">
                      {offer.name}
                    </h3>
                    <p className="text-sm text-gray-400">{offer.bonusHeadline}</p>
                  </div>
                  <FiArrowRight className="text-primary-green group-hover:translate-x-1 transition-transform" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Why UK Licensed Section */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Why Choose UK Licensed Casinos
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              UK Gambling Commission licensing ensures player protection, fair play, and secure transactions.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="glass-card p-6">
              <FiShield className="text-primary-green mb-4" size={32} />
              <h3 className="text-xl font-bold text-white mb-3">Player Protection</h3>
              <p className="text-gray-300">
                UKGC requires strict player protection measures, including deposit limits, self-exclusion tools, and responsible gambling resources.
              </p>
            </div>
            <div className="glass-card p-6">
              <FiLock className="text-primary-green mb-4" size={32} />
              <h3 className="text-xl font-bold text-white mb-3">Secure Transactions</h3>
              <p className="text-gray-300">
                All UK-licensed casinos must use SSL encryption and secure payment processing to protect your financial information.
              </p>
            </div>
            <div className="glass-card p-6">
              <FiCheck className="text-primary-green mb-4" size={32} />
              <h3 className="text-xl font-bold text-white mb-3">Fair Gaming</h3>
              <p className="text-gray-300">
                Regular audits ensure games are fair, random number generators are tested, and payout percentages are accurate.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Methods Overview */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Popular Payment Methods
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              UK casinos support a wide range of payment options for deposits and withdrawals.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: 'PayPal', speed: '24 hours', icon: FiCreditCard },
              { name: 'Skrill', speed: 'Instant', icon: FiCreditCard },
              { name: 'Bank Transfer', speed: '2-5 days', icon: FiDollarSign },
              { name: 'Visa/Mastercard', speed: '1-3 days', icon: FiCreditCard },
            ].map((method, index) => {
              const Icon = method.icon;
              return (
                <div key={index} className="glass-card p-6 text-center hover:border-primary-green transition-colors">
                  <Icon className="text-primary-green mx-auto mb-3" size={32} />
                  <h3 className="text-lg font-bold text-white mb-2">{method.name}</h3>
                  <p className="text-sm text-primary-green">{method.speed}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick Tips Section */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Quick Tips for Choosing a Casino
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Make informed decisions with these essential tips before you deposit.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Check the License',
                description: 'Always verify UK Gambling Commission license number on the casino website.',
                icon: FiShield
              },
              {
                title: 'Read Bonus Terms',
                description: 'Understand wagering requirements, time limits, and game restrictions before claiming.',
                icon: FiInfo
              },
              {
                title: 'Review Payout Speed',
                description: 'Check withdrawal processing times for your preferred payment method.',
                icon: FiClock
              },
              {
                title: 'Compare Offers',
                description: 'Don\'t just look at bonus amounts - compare overall value and terms.',
                icon: FiTrendingUp
              }
            ].map((tip, index) => {
              const Icon = tip.icon;
              return (
                <div key={index} className="glass-card p-6">
                  <Icon className="text-primary-green mb-4" size={28} />
                  <h3 className="text-xl font-bold text-white mb-3">{tip.title}</h3>
                  <p className="text-gray-300">{tip.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              What Makes a Casino Trustworthy
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Look for these indicators when evaluating casino reliability and safety.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="glass-card p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-primary-green/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <FiStar className="text-primary-green" size={24} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">High Trust Score</h3>
                  <p className="text-gray-300">
                    Our trust score combines licensing, security measures, payout history, and player feedback. 
                    Casinos scoring 90+ are considered highly trustworthy.
                  </p>
                </div>
              </div>
            </div>
            <div className="glass-card p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-primary-green/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <FiClock className="text-primary-green" size={24} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Fast Payout History</h3>
                  <p className="text-gray-300">
                    Consistent fast payouts indicate a well-managed casino. Look for casinos with 
                    proven track records of processing withdrawals within advertised timeframes.
                  </p>
                </div>
              </div>
            </div>
            <div className="glass-card p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-primary-green/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <FiDollarSign className="text-primary-green" size={24} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Transparent Terms</h3>
                  <p className="text-gray-300">
                    Trustworthy casinos clearly display bonus terms, wagering requirements, and 
                    withdrawal policies. Avoid casinos with hidden conditions or unclear rules.
                  </p>
                </div>
              </div>
            </div>
            <div className="glass-card p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-primary-green/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <FiShield className="text-primary-green" size={24} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Responsible Gambling Tools</h3>
                  <p className="text-gray-300">
                    Reputable casinos offer deposit limits, session timers, self-exclusion options, 
                    and links to support organizations. These tools show commitment to player welfare.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Responsible Gambling Section */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-card p-8 border-2 border-primary-green/30">
            <div className="flex items-center gap-4 mb-6">
              <FiAlertCircle className="text-primary-green" size={40} />
              <h2 className="text-3xl font-bold text-white">
                Responsible Gambling
              </h2>
            </div>
            <p className="text-gray-300 mb-6 text-lg leading-relaxed">
              Gambling should be entertainment, not a way to make money or solve financial problems. 
              Set limits, never chase losses, and only gamble with money you can afford to lose.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-black/50 p-4 rounded-lg">
                <h3 className="text-primary-green font-bold mb-2">Set Limits</h3>
                <p className="text-sm text-gray-400">
                  Use deposit limits, loss limits, and session time limits to stay in control.
                </p>
              </div>
              <div className="bg-black/50 p-4 rounded-lg">
                <h3 className="text-primary-green font-bold mb-2">Take Breaks</h3>
                <p className="text-sm text-gray-400">
                  Regular breaks help maintain perspective and prevent impulsive decisions.
                </p>
              </div>
              <div className="bg-black/50 p-4 rounded-lg">
                <h3 className="text-primary-green font-bold mb-2">Get Help</h3>
                <p className="text-sm text-gray-400">
                  If gambling becomes a problem, contact GambleAware, GamCare, or Gambling Therapy.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <a
                href="https://www.gambleaware.org"
                target="_blank"
                rel="noreferrer noopener"
                className="btn-secondary text-sm"
              >
                GambleAware
              </a>
              <a
                href="https://www.gamcare.org.uk"
                target="_blank"
                rel="noreferrer noopener"
                className="btn-secondary text-sm"
              >
                GamCare
              </a>
              <a
                href="https://gamblingtherapy.org"
                target="_blank"
                rel="noreferrer noopener"
                className="btn-secondary text-sm"
              >
                Gambling Therapy
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Games Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Popular Casino Games
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Discover the most popular games available at UK-licensed casinos.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { name: 'Slots', count: '2000+', description: 'Video slots with various themes' },
              { name: 'Blackjack', count: '50+', description: 'Classic card game variants' },
              { name: 'Roulette', count: '20+', description: 'European, American, French' },
              { name: 'Live Dealer', count: '150+', description: 'Real-time casino action' },
            ].map((game, index) => (
              <div key={index} className="glass-card p-6 text-center hover:border-primary-green transition-colors">
                <h3 className="text-2xl font-bold text-primary-green mb-2">{game.count}</h3>
                <h4 className="text-xl font-bold text-white mb-2">{game.name}</h4>
                <p className="text-sm text-gray-400">{game.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Preview Section */}
      <section id="faq" className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FAQAccordion />
        </div>
      </section>
    </>
  );
};

